// src/components/desk/ShareModal.jsx — mint a client link, and read what the
// client did with it. Spec: core/MEETINGS_MODEL.md §5.
//
// Two halves, deliberately in one place: minting a new share and the timeline of
// shares already sent for this property. Agents re-send the same property to many
// clients, so "who have I already sent this to, and did they look" is the same
// question as "send it again" — splitting them across two surfaces would mean an
// agent re-sends blind.
//
// ⚠️ The price entered here is FROZEN on the share. It is not the listing price
// and it never updates — that is the point (a communication artifact, not a live
// view). The field is seeded from the listing purely as a convenience.

import { useCallback, useEffect, useState } from 'react'
import { useDeliberateBackdropDismiss } from '../../hooks/useDeliberateBackdropDismiss.js'
import { t, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { input, btn } from './primitives.jsx'
import { timeAgo } from './primitives.jsx'
import { createPropertyShare, getPropertyShares, revokePropertyShare, propertyShareUrl, getPropertyShareSeed, findPartyCandidates } from '../../db/core.js'

const CCY = ['USD', 'GEL', 'EUR']

// One line of CRM intelligence per share. "Opened 7 times, last viewed yesterday
// 21:43, viewed the photos, clicked the map twice" tells an agent far more than
// "the client hasn't replied" — which is the only thing they know today.
function Engagement({ e }) {
  if (!e || !e.opens) return <span className="text-muted-foreground italic">{t('share_not_opened')}</span>
  const bits = [
    tLog('share_opens', { n: e.opens }),
    e.last_opened_at ? tLog('share_last_viewed', { when: timeAgo(e.last_opened_at) }) : null,
    e.devices_approx > 1 ? tLog('share_devices', { n: e.devices_approx }) : null,
    e.gallery_viewed ? t('share_saw_photos') : null,
    e.map_opened ? tLog('share_map', { n: e.map_opened }) : null,
    e.call_clicked ? tLog('share_called', { n: e.call_clicked }) : null,
    e.whatsapp_clicked ? tLog('share_whatsapped', { n: e.whatsapp_clicked }) : null,
  ].filter(Boolean)
  return (
    <span className={e.meeting_requested ? 'text-emerald-600 dark:text-emerald-400 font-medium' : ''}>
      {e.meeting_requested ? `${t('share_requested_viewing')} · ` : ''}{bits.join(' · ')}
    </span>
  )
}

export function ShareModal({ propertyId, propertyLabel, seedPrice, seedCurrency, meetingId, recipientPartyId, recipientName, onClose }) {
  const backdropDismiss = useDeliberateBackdropDismiss(onClose)
  const [price, setPrice] = useState(seedPrice ?? '')
  const [currency, setCurrency] = useState(seedCurrency || 'USD')
  // Max occupants, and whether this property is even a rental. Both are fetched
  // rather than passed: neither call site has listing_terms loaded, and a third
  // call site added later would silently seed an empty box — which reads as "no
  // limit stated" for a property that has one recorded. `intent` is null until the
  // seed lands, so the field appears only once we know it is rent-shaped rather
  // than flashing onto a sale.
  const [maxOccupants, setMaxOccupants] = useState('')
  const [seedIntent, setSeedIntent] = useState(null)
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [minted, setMinted] = useState(null)   // { token, address_precision }
  const [copied, setCopied] = useState(false)
  const [shares, setShares] = useState([])
  // PRIVATE note about who this link is going to (0379). Sits beside `message` in state and
  // one field away in the form, and they are opposites: `message` is written FOR the client
  // and rendered on their page, this is written ABOUT them and never leaves the agency.
  const [memo, setMemo] = useState('')

  // ── WHO is this link for? OPTIONAL, and optional is the ruling (B397) ────────
  // Owner ruling 2026-08-07, asked as forced-vs-optional and answered: *"not force,
  // but make it optional to select their existing client or add a new one — just
  // name or phone or both, whichever they want."*
  //
  // WHY IT EXISTS: 314 of 318 shares named nobody, because only ONE of this modal's
  // three call sites had a client in hand to pass (the meeting-anchored door). The
  // common door — the 🔗 chip on a property card — genuinely has no client in
  // context, so there was nothing to pass and nothing to fix in the wiring. The
  // agent knows who they are sending it to; this is the first place we ask.
  //
  // ⚠️ NOTHING HERE MAY BLOCK THE MINT. A link with no recipient stays a first-class
  // outcome and stays exactly as fast to send — that is the difference between the
  // ruling and the version of this that was rejected.
  const [pickName, setPickName] = useState('')
  const [pickPhone, setPickPhone] = useState('')
  const [picked, setPicked] = useState(null)      // a candidate the agent CHOSE
  const [cands, setCands] = useState([])
  // The caller already knows (the meeting door). Then this is settled, not asked.
  const askRecipient = !recipientPartyId

  // ⚠️ THE PICK IS THE HUMAN VERDICT (0027/0123), and it is the only sanctioned way
  // to reuse an existing party — silent reuse stays forbidden. So this SHOWS matches
  // and never auto-selects one, however confident the phone match looks.
  // Debounced because it fires per keystroke of a phone number, which is the shape
  // that made the Desk's identity reach-back cost ~2.9s PER DIGIT (2026-08-05).
  useEffect(() => {
    if (!askRecipient || picked) return
    const digits = pickPhone.replace(/\D/g, '')
    if (digits.length < 6) { setCands([]); return }
    let alive = true
    const h = setTimeout(() => {
      findPartyCandidates({ phone: pickPhone.trim() })
        .then(r => { if (alive) setCands((r?.candidates || []).filter(c => !c.is_agent).slice(0, 4)) })
        .catch(() => { if (alive) setCands([]) })   // soft: typing a new client is still open
    }, 400)
    return () => { alive = false; clearTimeout(h) }
  }, [pickPhone, picked, askRecipient])

  const load = useCallback(() => {
    getPropertyShares({ propertyId }).then(setShares).catch(() => {})
  }, [propertyId])
  useEffect(() => { load() }, [load])

  // Inherit what the listing already says. Seeds ONCE on open and never again: the
  // agent may deliberately clear the box (this client was told no limit), and a
  // re-seed on any later render would type the owner's number back in underneath
  // them. A failed seed leaves the box empty and mintable — never blocks the link.
  useEffect(() => {
    let alive = true
    getPropertyShareSeed(propertyId)
      .then(s => {
        if (!alive) return
        setSeedIntent(s?.intent || null)
        if (s?.max_occupants != null) setMaxOccupants(String(s.max_occupants))
      })
      .catch(() => {})
    return () => { alive = false }
  }, [propertyId])

  // Rent-shaped only. girao sits with sale, not rent: the sum is handed over and
  // returned, so it is a purchase-shaped deal and an occupants cap is meaningless
  // on it. Same split the server makes — if one side changes, change both.
  const isRental = seedIntent === 'rent' || seedIntent === 'daily_rent'

  async function mint() {
    if (busy) return
    setBusy(true); setErr('')
    try {
      const out = await createPropertyShare({
        propertyId, meetingId,
        // Precedence, and it is deliberate: the caller's own recipient (the meeting
        // door) > a candidate the agent PICKED > whatever they typed. The typed
        // fields are sent only when no party is settled, so a pick can never be
        // shadowed by leftover text in a box the pick hid.
        recipientPartyId: recipientPartyId || picked?.party_id || null,
        recipientName: recipientPartyId || picked ? null : (pickName.trim() || null),
        recipientPhone: recipientPartyId || picked ? null : (pickPhone.trim() || null),
        offeredPrice: price === '' ? null : Number(price),
        offeredCurrency: price === '' ? null : currency,
        // Never sent on a sale, even if a stale seed left a number in state — the
        // field is hidden there, and a hidden field must not still submit.
        offeredMaxOccupants: isRental && maxOccupants !== '' ? Number(maxOccupants) : null,
        message,
        senderMemo: memo,
      })
      setMinted(out)
      load()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  async function copy(token) {
    try {
      await navigator.clipboard.writeText(propertyShareUrl(token))
      setCopied(true); setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard blocked — the URL is selectable in the field below */ }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      {...backdropDismiss}>
      <div className="bg-card border border-border rounded-xl w-full max-w-lg shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border shrink-0">
          <span className="font-semibold text-sm"><Icon name="link" size={15} /> {t('share_title')}</span>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">&times;</button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
          {propertyLabel && <p className="text-sm text-muted-foreground truncate">{propertyLabel}</p>}
          {/* Who this is going to, when the surface knows — a match row does, a
              bare property card doesn't. Recorded on the share, so "already sent"
              can later say WHO rather than just how many. */}
          {recipientName && (
            <p className="text-xs text-muted-foreground">
              <Icon name="user" size={12} /> {tLog('share_recipient', { name: recipientName })}
            </p>
          )}

          {minted ? (
            <div className="space-y-2">
              <p className="text-sm">{t('share_ready')}</p>
              <div className="flex gap-2">
                <input readOnly className={input} value={propertyShareUrl(minted.token)}
                  onFocus={e => e.target.select()} />
                <button className={btn('primary')} onClick={() => copy(minted.token)}>
                  {copied ? t('share_copied') : t('share_copy')}
                </button>
              </div>
              {/* Told plainly, because the agent is about to send it and would
                  otherwise assume the client sees everything they see. */}
              {minted.address_precision === 'district' && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  <Icon name="alert-triangle" size={11} /> {t('share_district_only')}
                </p>
              )}
              <button className={btn('ghost')} onClick={() => { setMinted(null); setMessage('') }}>
                {t('share_another')}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {/* ── Who is this for? OPTIONAL (B397) ──────────────────────────
                  The label says optional out loud rather than relying on the
                  absence of a required marker: an agent who reads this as a new
                  mandatory step is the failure the owner ruled against, and it
                  costs them a client link they would otherwise have sent.
                  Placed FIRST because it is who, and everything below it is what
                  — but it is the only block on the form that submits empty
                  without consequence. */}
              {askRecipient && (
                <div className="space-y-1.5 pb-1">
                  <label className="block text-xs tracking-[.02em] text-muted-foreground">
                    {t('share_for_label')}
                  </label>
                  {picked ? (
                    /* Settled. The inputs are GONE, not disabled — a box you can
                       still type into next to a chosen name is a question about
                       which one wins, and the answer must never be in doubt. */
                    <div className="flex items-center gap-2 text-sm">
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-accent">
                        <Icon name="user" size={12} />
                        {picked.display_name || picked.primary_phone}
                      </span>
                      <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline"
                        onClick={() => { setPicked(null); setCands([]) }}>
                        {t('share_for_change')}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <input className={input + ' min-w-0'} value={pickName} placeholder={t('share_for_name_ph')}
                          autoComplete="off" onChange={e => setPickName(e.target.value)} />
                        <input className={input + ' min-w-0'} inputMode="tel" value={pickPhone}
                          placeholder={t('share_for_phone_ph')} autoComplete="off"
                          onChange={e => setPickPhone(e.target.value)} />
                      </div>
                      {/* Existing clients on that number. Shown, never auto-picked:
                          the agent's explicit tap IS the human verdict 0027 requires,
                          and silent reuse of a party stays forbidden. */}
                      {cands.length > 0 && (
                        <div className="rounded-lg border border-border divide-y divide-border">
                          {cands.map(c => (
                            <button key={c.party_id} type="button"
                              className="w-full text-left px-2 py-1.5 hover:bg-accent transition-colors"
                              onClick={() => { setPicked(c); setCands([]) }}>
                              <span className="block text-[13px]">{c.display_name || c.primary_phone}</span>
                              <span className="block text-[12px] text-muted-foreground">
                                {[c.primary_phone,
                                  c.requirements_count ? tLog('share_for_reqs', { n: c.requirements_count }) : null,
                                ].filter(Boolean).join(' · ')}
                              </span>
                            </button>
                          ))}
                          <p className="px-2 py-1 text-[12px] text-muted-foreground">{t('share_for_or_new')}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <label className="block text-xs tracking-[.02em] text-muted-foreground">{t('share_price_label')}</label>
              {/* ⚠️ The WRAPPER carries the width, not the select. `input` hard-codes
                  w-full and Tailwind emits .w-full AFTER the numeric widths, so the
                  ' w-28' this used to append lost the cascade and did nothing — the
                  select rendered full-width and the row split roughly 50/50 instead
                  of "wide price, narrow currency". Same trap as the occupants field
                  below; verified against the built CSS, not assumed. */}
              <div className="flex gap-2">
                <input className={input + ' min-w-0'} inputMode="numeric" value={price} placeholder={t('share_price_ph')}
                  onChange={e => setPrice(e.target.value.replace(/[^\d.]/g, ''))} />
                <div className="w-28 shrink-0">
                  <select className={input} value={currency} onChange={e => setCurrency(e.target.value)}>
                    {CCY.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              {/* The frozen-price rule, stated where the decision is made. */}
              <p className="text-xs text-muted-foreground">{t('share_price_frozen')}</p>

              {/* Rentals only. Seeded from listing_terms.max_occupants, then frozen
                  on the share exactly like the price — the agent may state 5 to this
                  family where the owner's terms say 4, without amending the terms for
                  everyone. Left empty = not stated, which prints nothing anywhere;
                  it does NOT mean "no limit". */}
              {isRental && (
                <>
                  <label className="block text-xs tracking-[.02em] text-muted-foreground pt-1">
                    {t('share_max_occupants_label')}
                  </label>
                  {/* Sized to its content: this holds at most two digits, so a
                      full-width box read as a text field and made the modal look
                      broken. The "empty means not stated" note sits BESIDE it rather
                      than in a placeholder — a placeholder disappears the moment the
                      seed fills the box, which is exactly when the agent is deciding
                      whether to clear it. */}
                  {/* ⚠️ The WRAPPER carries the width, not the input. `input` hard-codes
                      w-full, and Tailwind emits w-full AFTER the numeric widths — so
                      `input + ' w-20'` loses the cascade and silently does nothing.
                      A fixed-width parent works whatever the primitive contains. */}
                  <div className="flex items-center gap-2">
                    <div className="w-20 shrink-0">
                      <input className={input} inputMode="numeric" value={maxOccupants}
                        onChange={e => setMaxOccupants(e.target.value.replace(/\D/g, '').slice(0, 2))} />
                    </div>
                    <span className="text-xs text-muted-foreground">{t('share_max_occupants_empty')}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{t('share_max_occupants_hint')}</p>
                </>
              )}

              {/* ── TWO NOTES, AND THEY MUST NEVER BE CONFUSED (0379) ──────────────────
                  These are the only two free-text boxes on the form and they point in
                  opposite directions, so they are labelled by AUDIENCE, not by name, and
                  each carries the icon of who reads it:
                    👁  message      → the client reads this, on their page.
                    🔒  sender_memo  → only the agency reads this. Who they are, in the
                                       agent's own words ("Nino's husband, wants 2nd floor+").
                  The private one is tinted and rule-bordered so the difference survives a
                  glance — an agent typing "difficult, lowball offers" into the wrong box
                  sends it straight to the person it is about, and no amount of careful
                  labelling undoes that once it is sent. Client-facing box first, because it
                  is the one that is part of sending; the memo is bookkeeping. */}
              <label className="flex items-center gap-1.5 text-xs tracking-[.02em] text-muted-foreground pt-1">
                <Icon name="eye" size={12} /> {t('share_message_label')}
              </label>
              <textarea className={input} rows={3} value={message} placeholder={t('share_message_ph')}
                onChange={e => setMessage(e.target.value)} />

              <label className="flex items-center gap-1.5 text-xs tracking-[.02em] text-amber-600 dark:text-amber-400 pt-1">
                <Icon name="lock" size={12} /> {t('share_memo_label')}
              </label>
              <textarea
                className={`${input} border-amber-500/40 bg-amber-500/5 focus:border-amber-500/60`}
                rows={2} value={memo} placeholder={t('share_memo_ph')}
                onChange={e => setMemo(e.target.value)} />
              <p className="text-xs text-amber-600/80 dark:text-amber-400/80">{t('share_memo_hint')}</p>
              {err && <p className="text-xs text-destructive">{err}</p>}
              <button className={btn('primary') + ' w-full'} disabled={busy} onClick={mint}>
                {busy ? t('share_creating') : t('share_create_btn')}
              </button>
            </div>
          )}

          {shares.length > 0 && (
            <div className="border-t border-border pt-3">
              <p className="text-xs tracking-[.02em] text-muted-foreground mb-1.5">{t('share_sent_before')}</p>
              <div className="space-y-1.5">
                {shares.map(s => {
                  const dead = s.revoked_at || s.expired_at
                  return (
                    <div key={s.id} className={`rounded-lg border px-3 py-2 text-xs ${dead ? 'border-border/50 opacity-60' : 'border-border'}`}>
                      <div className="flex items-center gap-2">
                        {/* WHO leads the row now that a memo exists — "already sent" is a
                            list of PEOPLE, and an agent scanning it is looking for a name,
                            not a number. 🔒 rides it wherever it renders, so the private note
                            never reads as something the client was shown. No fallback to the
                            structured recipient: the rollup returns recipient_party_id and no
                            name, so rendering one here would be inventing a field. */}
                        {s.sender_memo && (
                          <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium min-w-0"
                            title={s.sender_memo}>
                            <Icon name="lock" size={11} />
                            <span className="truncate max-w-[10rem]">{s.sender_memo}</span>
                          </span>
                        )}
                        <span className="font-medium">
                          {s.offered_price != null
                            ? `${Number(s.offered_price).toLocaleString('en-US')} ${s.offered_currency || ''}`.trim()
                            : t('share_price_on_request')}
                        </span>
                        <span className="text-muted-foreground">· {timeAgo(s.created_at)}</span>
                        <span className="flex-1" />
                        {!dead && (
                          <>
                            {/* OPEN — see exactly what the client sees. An agent quoting from
                                this row should be able to check the page still renders (photos
                                loaded, address precision as expected) without pasting the URL
                                somewhere. Opens the real client page, not a preview: a preview
                                would be a second rendering to keep in sync, and the whole point
                                is to look at the artifact that was actually sent. */}
                            <a href={propertyShareUrl(s.token)} target="_blank" rel="noopener noreferrer"
                              className="text-primary underline decoration-dotted"
                              onClick={e => e.stopPropagation()}>{t('share_open')}</a>
                            <button className="text-primary underline decoration-dotted" onClick={() => copy(s.token)}>
                              {t('share_copy')}
                            </button>
                            {/* REMOVE = revoke. The link stops working immediately; the ROW
                                stays, labelled. There is deliberately no hard delete: the
                                client may still be arguing from the number this link gave
                                them, and an agent who cannot see it sent cannot answer them. */}
                            <button className="text-destructive underline decoration-dotted"
                              onClick={() => revokePropertyShare(s.id).then(load).catch(() => {})}>
                              {t('share_revoke')}
                            </button>
                          </>
                        )}
                        {dead && <span className="text-muted-foreground">{t('share_revoked')}</span>}
                      </div>
                      <div className="mt-0.5 text-muted-foreground"><Engagement e={s.engagement} /></div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
